import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Input, Select } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Button from "@mui/material/Button";
import { useSelector } from "react-redux";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { LeadsPDF } from "../../../../../helpers/pdfGenerator";
import { leadListManipulator } from "../../../../../helpers/manuplator";
import { CSVLink } from "react-csv";
import http from "../../../../../services/http";
import { recordActions } from "../../../../../constant";
import { BeatLoader } from "react-spinners";
import { salesforceOauth } from "../../../../../utils";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import GeneratingPdfModal from "../../../../../components/generating-pdf-modal";

const LeadsHeader = ({
  search,
  setSearch,
  listFilter,
  setListFilter,
  total,
  getLeadsHelper,
}) => {
  const CSV_HEADERS = [
    { label: "Name", key: "name" },
    { label: "URL", key: "website" },
    { label: "State", key: "state" },
    { label: "Country", key: "country" },
    { label: "Status", key: "status" },
    { label: "User Notes", key: "notes" },
  ];

  // STATES
  const [searchValue, setSearchValue] = useState(search);
  const [isPdfLoading, setPdfLoading] = useState(false);
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [isCsvLoading, setCsvLoading] = useState(false);
  const [isCrmExportLoding, setCrmExportLoding] = useState(false);
  const [selectedLeads, setselectedLeads] = useState([]);

  // REDUX DATA
  const selectedCompany = useSelector((state) => state.CompanyReducer.selected_company);
  const user = useSelector((state) => state.UserReducer.profile);
  const list = useSelector((state) => state.LeadsListReducer.leadsList);

  // CONST VALS
  const isBtnDisabled = total?.length < 1;
  const downloadCsvBtn = useRef(null);
  const isEnterpriseUser = user?.role === "enterprise";
  const { search: searchParam } = useLocation();
  const searchParams = new URLSearchParams(searchParam);
  const exportToCRM = searchParams.get("connected");

  // HANDLERS
  const setSearchHandler = useCallback(
    debounce((value) => setSearch(value), 500),
    [],
  );
  const searchHandler = (value) => {
    setSearchValue(value);
    setSearchHandler(value);
  };
  const downloadPDF = (event) => {
    event.preventDefault();
    setPdfLoading(true);
    setPdfProgress(0);
    setPdfModalVisible(true);
    const progressInterval = setInterval(() => {
      setPdfProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + 8;
      });
    }, 120);
    getLeadsHelper({ limit: total.length, offset: 0 })
      .then(async ({ data }) => {
        const blob = await pdf(
          <LeadsPDF
            data={data.leads}
            company={selectedCompany?.company_name}
          />,
        ).toBlob();
        clearInterval(progressInterval);
        setPdfProgress(100);
        http.post("action-record", {
          action: recordActions.leadsPDF,
        });
        setPdfModalVisible(false);
        saveAs(blob, `${selectedCompany?.company_name} Leads.pdf`);
      })
      .catch((e) => {
        clearInterval(progressInterval);
        setPdfModalVisible(false);
      })
      .finally(() => {
        setPdfLoading(false);
      });
  };

  const downloadCsv = () => {
    setCsvLoading(true);
    getLeadsHelper({ limit: total.length, offset: 0 }).then(({ data }) => {
      setselectedLeads(data.leads);
      setTimeout(() => {
        downloadCsvBtn?.current?.link.click();
        setCsvLoading(false);
        http.post("action-record", {
          action: recordActions.leadsCSV,
        });
      }, 100);
    });
  };

  const exportInvestorsToCRM = async () => {
    setCrmExportLoding(true);
    http
      .get(`user-lead`, {
        user_company_id: selectedCompany.id,
        crm_export: true,
      })
      .then(({ data }) => {
        toast.success("Leads exported to CRM successfully");
        http.post("action-record", {
          action: recordActions.leadsCRMExport,
        });
      })
      .finally(() => {
        setCrmExportLoding(false);
        searchParams.delete("connected");
        window.history.replaceState(null, "", window.location.pathname);
      })
      .catch((err) => {
        const isUnauthorized = err?.response?.status === 403;
        if (isUnauthorized) {
          salesforceOauth("/leads-manager");
          return;
        }
        toast.error(err?.response?.data?.error ?? "Failed to export to CRM");
      });
  };

  // HOOKS
  useEffect(() => {
    if (exportToCRM) {
      exportInvestorsToCRM();
    }
  }, [exportToCRM]);

  return (
    <>
      <div className="top-header">
      <div style={{ display: "none" }}>
        <CSVLink
          data={leadListManipulator(selectedLeads)}
          ref={downloadCsvBtn}
          // TODO: uncomment this when we restrict professional users from accessing enterprise features
          // filename={`${isEnterpriseUser ? selectedCompany?.company_name : "Conceptor"}` + " Leads"}
          filename={`${selectedCompany?.company_name} Leads`}
          headers={CSV_HEADERS}
        />
      </div>
      <div className="filter-box">
        <div className={`input-box`}>
          <span className="prefix">
            <SearchOutlined />
          </span>
          <Input
            value={searchValue}
            className="search-box"
            placeholder="Name"
            onChange={(e) => searchHandler(e.target.value)}
          />
        </div>
        <div className="list-filter">
          <Select
            value={listFilter}
            onChange={(value) => setListFilter(value)}
            placeholder="Filter by status"
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            <Select.Option value="default">General</Select.Option>
            {list?.map((item) => (
              <Select.Option value={item.id} key={item.key}>
                {item.name}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>
      <div className="action-box">
        <Button
          className="download-btn"
          disabled={isBtnDisabled || isPdfLoading}
          onClick={downloadPDF}
        >
          {isPdfLoading ? (
            <BeatLoader className="loader" color="#fff" size={7} />
          ) : (
            "Download PDF"
          )}
        </Button>
        <Button
          className="download-btn"
          onClick={downloadCsv}
          disabled={isBtnDisabled || isCsvLoading}
        >
          {isCsvLoading ? (
            <BeatLoader className="loader" color="#fff" size={7} />
          ) : (
            "Download CSV"
          )}
        </Button>
        {/* {isEnterpriseUser && (
            <Button
              className="download-btn"
              disabled={isBtnDisabled || isCrmExportLoding}
              onClick={exportInvestorsToCRM}
            >
              {isCrmExportLoding ? (
                <BeatLoader className="loader" color="#fff" size={7} />
              ) : (
                "Export to CRM"
              )}
            </Button>
          )} */}
      </div>
    </div>
    <GeneratingPdfModal
      visible={pdfModalVisible}
      onClose={() => setPdfModalVisible(false)}
      progress={pdfProgress}
    />
    </>
  );
};

export default LeadsHeader;
